<template>
  <b-modal
    id="org-settings"
    centered
    hide-header
    hide-footer
    @show="reset"
    @hidden="reset"
  >
    <h3 class="mb-3">Security Settings</h3>
    <form @submit.prevent="save">
      <label>
        Authentication Expiration Time
        <b-select v-model="form.auth_expiry_minutes" :options="expiryOptions" />
      </label>

      <b-checkbox v-model="form.require_2fa" class="my-2" switch>
        Require 2FA for all users
      </b-checkbox>

      <b-checkbox v-model="form.require_password_change" class="my-2" switch>
        Require password to be changed every 90 days
      </b-checkbox>

      <b-checkbox v-model="form.prevent_password_reuse" class="my-2" switch>
        Prevent users from reusing previous passwords
      </b-checkbox>

      <b-checkbox v-model="form.enable_ip_whitelist" class="my-2" switch>
        Restrict Nanome to IP addresses in the whitelist
      </b-checkbox>

      <template v-if="form.enable_ip_whitelist">
        <h4 class="mt-4">IP Whitelist</h4>
        <div class="small">
          Only IP addresses listed here will be allowed to log in to the Nanome
          app
        </div>
        <b-table
          v-if="form.ip_whitelist.length"
          class="mt-3"
          :fields="[
            { key: 'ip', label: 'IP' },
            { key: 'name', value: 'name' },
            { key: 'actions', label: '' }
          ]"
          :items="form.ip_whitelist"
        >
          <template #cell(ip)="{ item }">
            <NanInput v-model="item.ip" placeholder="IP address" autofocus />
          </template>

          <template #cell(name)="{ item }">
            <NanInput v-model="item.name" placeholder="Name" />
          </template>

          <template #cell(actions)="{ index }">
            <b-btn
              v-b-tooltip="'Remove IP'"
              variant="link"
              @click="removeIP(index)"
            >
              <i class="icon-trash" />
            </b-btn>
          </template>
        </b-table>
        <div v-else>No IPs whitelisted yet</div>

        <b-btn class="mt-3" size="sm" variant="outline-primary" @click="addIP">
          <i class="icon-plus" />
          Add IP Address
        </b-btn>
      </template>

      <div class="mt-4 text-right">
        <b-btn
          class="ml-2 px-4"
          variant="secondary"
          @click="$bvModal.hide('org-settings')"
        >
          Cancel
        </b-btn>
        <b-btn
          :disabled="!hasChanges"
          class="ml-2 px-4"
          variant="primary"
          type="submit"
        >
          Save
        </b-btn>
      </div>
    </form>
  </b-modal>
</template>

<script>
import API from "@/api";

export default {
  props: {
    org: {
      type: Object,
      required: true
    }
  },

  data: () => ({
    expiryOptions: [
      { value: 30, text: "30 minutes" },
      { value: 60, text: "1 hour" },
      { value: 24 * 60, text: "1 day" },
      { value: 7 * 24 * 60, text: "7 days" },
      { value: 30 * 24 * 60, text: "30 days" }
    ],
    form: {
      require_2fa: false,
      require_password_change: false,
      prevent_password_reuse: false,
      enable_ip_whitelist: false,
      ip_whitelist: [],
      auth_expiry_minutes: 30 * 24 * 60
    }
  }),

  computed: {
    changedSettings() {
      const settings = {};
      for (let key in this.form) {
        let value = this.form[key];
        if (typeof value === "object") {
          value = JSON.stringify(value);
        }
        if (value !== this.org.settings[key]) {
          settings[key] = value;
        }
      }
      return settings;
    },
    hasChanges() {
      return Object.keys(this.changedSettings).length > 0;
    }
  },

  methods: {
    addIP() {
      this.form.ip_whitelist.push({ ip: "", name: "" });
      document.activeElement.blur();
    },

    removeIP(index) {
      this.form.ip_whitelist.splice(index, 1);
    },

    reset() {
      this.form.require_2fa = this.org.settings.require_2fa;
      this.form.require_password_change = this.org.settings.require_password_change;
      this.form.prevent_password_reuse = this.org.settings.prevent_password_reuse;
      this.form.enable_ip_whitelist = this.org.settings.enable_ip_whitelist;
      this.form.ip_whitelist = JSON.parse(this.org.settings.ip_whitelist);
      this.form.auth_expiry_minutes = this.org.settings.auth_expiry_minutes;
    },

    async save() {
      const data = this.changedSettings;
      if ("auth_expiry_minutes" in data) {
        const res = await this.$bvModal.msgBoxConfirm(
          "Changing authentication expiration time will log out all org members. Continue?",
          {
            title: "Confirm Change",
            okTitle: "Yes",
            okVariant: "danger",
            size: "sm",
            centered: true
          }
        );
        if (!res) {
          this.form.auth_expiry_minutes = this.org.settings.auth_expiry_minutes;
          return;
        }
      }

      const { success } = await API.updateOrgSettings(this.org.id, data);
      if (!success) return;

      this.$emit("updated");
      this.$notify({
        type: "success",
        text: "Organization settings saved"
      });
    }
  }
};
</script>
